import Analytics from '@/utils/analytics';
import React, { useMemo, useRef } from 'react';
import { ActivityIndicator } from './activity-indicator';
import {
  ActivityIndicatorProps,
  Insets,
  LayoutChangeEvent,
  Role,
  StyleProp,
  StyleSheet,
  Text,
  TextProps,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { FONTS, ThemedSVGProps, TYPOGRAPHY } from '@/styles';
import { SvgProps } from 'react-native-svg';
import { Theme, useTheme } from '@/contexts/theme-context';
import { TrackingData } from '@/utils/analytics/base-reporter';
import { useHover } from 'react-native-web-hooks';

export type Size = 'xsmall' | 'small' | 'medium' | 'large';
export type Type = 'primary' | 'secondary' | 'tertiary' | 'danger';

export const SIZES: Record<Size, number> = {
  large: 60,
  medium: 52,
  small: 36,
  xsmall: 24,
};

const SIZE_PADDING_HORIZONTAL: Record<Size, number> = {
  large: 24,
  medium: 16,
  small: 8,
  xsmall: 16,
};

const SIZE_ELEMENT_CONTAINER_MARGIN: Record<Size, number> = {
  large: 32,
  medium: 32,
  small: 12,
  xsmall: 12,
};

const BORDER_WIDTH = 2;

const getThemeStyleValues = (theme: Theme) => {
  const fontSizeMap: Record<Size, number> = {
    large: theme.fontSizes.medium,
    medium: theme.fontSizes.medium,
    small: theme.fontSizes.medium,
    xsmall: theme.fontSizes.small,
  };

  const backgroundColorMap: Record<Type, string> = {
    danger: theme.colors.white,
    primary: theme.colors.primary,
    secondary: theme.colors.transparent,
    tertiary: theme.colors.transparent,
  };

  const disabledBackgroundColorMap: Record<Type, string> = {
    danger: theme.colors.transparent,
    primary: theme.colors.inactiveFill,
    secondary: theme.colors.transparent,
    tertiary: theme.colors.transparent,
  };

  const textColorMap: Record<Type, string> = {
    danger: theme.colors.negative,
    primary: theme.colors.white,
    secondary: theme.colors.primary,
    tertiary: theme.colors.primary,
  };

  const disabledTextColorMap: Record<Type, string> = {
    danger: theme.colors.inactiveFill,
    primary: theme.colors.white,
    secondary: theme.colors.inactiveFill,
    tertiary: theme.colors.inactiveFill,
  };

  return {
    backgroundColorMap,
    disabledBackgroundColorMap,
    disabledTextColorMap,
    fontSizeMap,
    textColorMap,
  };
};

const getLeftElementContainerStyles = (size: Size) => {
  return {
    marginRight: SIZE_ELEMENT_CONTAINER_MARGIN[size],
  };
};

const getRightElementContainerStyles = (size: Size) => {
  return {
    marginLeft: SIZE_ELEMENT_CONTAINER_MARGIN[size],
  };
};

type ModifiedTextProps = Omit<TextProps, 'style'>;

export interface Props {
  accessibilityLabel?: string;
  accessibilityRole?: Role;
  children?: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  disabled?: boolean;
  focusable?: boolean;
  hitSlop?: Insets;
  hoverStyle?: StyleProp<ViewStyle>;
  Icon?: React.FC<SvgProps>;
  iconProps?: ThemedSVGProps;
  label?: string | null;
  labelProps?: ModifiedTextProps;
  labelStyle?: StyleProp<TextStyle>;
  leftElement?: React.ReactNode;
  leftElementStyle?: StyleProp<ViewStyle>;
  loading?: boolean;
  loadingProps?: ActivityIndicatorProps;
  onLayout?: (event: LayoutChangeEvent) => void;
  onPress: () => void;
  rightElement?: React.ReactNode;
  rightElementStyle?: StyleProp<ViewStyle>;
  rounded?: boolean;
  size?: Size;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  textProps?: ModifiedTextProps;
  textStyle?: StyleProp<TextStyle>;
  trackingData: TrackingData | null;
  type?: Type;
}

export const Button = React.forwardRef<View, Props>(
  (
    {
      accessibilityLabel,
      accessibilityRole = 'button',
      children,
      containerStyle,
      contentStyle,
      disabled = false,
      focusable = true,
      hitSlop,
      hoverStyle,
      Icon,
      iconProps = {},
      label,
      labelProps,
      labelStyle,
      leftElement,
      leftElementStyle,
      loading = false,
      loadingProps = {},
      onLayout,
      onPress,
      rightElement,
      rightElementStyle,
      rounded = false,
      size = 'medium',
      style = {},
      testID,
      textProps,
      textStyle,
      trackingData,
      type = 'primary',
    },
    ref,
  ) => {
    const theme = useTheme();

    const touchableOpacityRef =
      useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
    const isHovered = useHover(touchableOpacityRef);

    const themeValues = useMemo(() => {
      const {
        backgroundColorMap,
        disabledBackgroundColorMap,
        disabledTextColorMap,
        fontSizeMap,
        textColorMap,
      } = getThemeStyleValues(theme);

      const baseHeight = SIZES[size];
      const height = rounded ? baseHeight + 2 * BORDER_WIDTH : baseHeight;
      const textColor = textColorMap[type];
      return {
        baseStyle: {
          backgroundColor: disabled
            ? disabledBackgroundColorMap[type]
            : backgroundColorMap[type],
          borderColor:
            type === 'danger' || type === 'tertiary'
              ? theme.colors.transparent
              : disabled
                ? theme.colors.inactiveFill
                : theme.colors.primary,
          borderRadius: rounded ? height : 4,
          borderWidth: rounded ? 0 : BORDER_WIDTH,
          height,
          paddingHorizontal: SIZE_PADDING_HORIZONTAL[size],
        },
        iconProps: {
          fill: textColor,
          stroke: textColor,
        },
        labelStyle: {
          color: disabled
            ? theme.sk.color.text.disabled
            : theme.sk.color.text.tertiary,
        },
        textStyle: {
          color: disabled ? disabledTextColorMap[type] : textColor,
          fontSize: fontSizeMap[size],
        },
      };
    }, [disabled, rounded, size, theme, type]);

    let content = (
      <TouchableOpacity
        accessibilityLabel={accessibilityLabel}
        activeOpacity={0.8}
        disabled={disabled || loading}
        focusable={focusable}
        hitSlop={hitSlop}
        onLayout={onLayout}
        onPress={() => {
          if (trackingData) {
            Analytics.track(
              `${trackingData.name}.press`,
              trackingData.properties,
            );
          }

          onPress();
        }}
        ref={touchableOpacityRef}
        role={accessibilityRole}
        testID={testID}
      >
        <View
          ref={ref}
          style={[themeValues.baseStyle, style, isHovered ? hoverStyle : null]}
        >
          <View
            style={[
              styles.content,
              loading ? styles.contentLoading : null,
              contentStyle,
            ]}
          >
            {leftElement ? (
              <View
                style={[getLeftElementContainerStyles(size), leftElementStyle]}
              >
                {leftElement}
              </View>
            ) : null}
            {Icon ? (
              <Icon {...themeValues.iconProps} {...iconProps} />
            ) : label ? (
              <View style={styles.textContainer}>
                <Text
                  numberOfLines={1}
                  {...labelProps}
                  style={[
                    FONTS.primaryBold,
                    themeValues.textStyle,
                    TYPOGRAPHY.smallLabel,
                    styles.label,
                    themeValues.labelStyle,
                    labelStyle,
                  ]}
                >
                  {label}
                </Text>
                <Text
                  {...textProps}
                  style={[
                    FONTS.primaryBold,
                    themeValues.textStyle,
                    styles.labelledValue,
                    textStyle,
                  ]}
                >
                  {children}
                </Text>
              </View>
            ) : (
              <Text
                {...textProps}
                style={[FONTS.primaryBold, themeValues.textStyle, textStyle]}
              >
                {children}
              </Text>
            )}
            {rightElement ? (
              <View
                style={[
                  getRightElementContainerStyles(size),
                  rightElementStyle,
                ]}
              >
                {rightElement}
              </View>
            ) : null}
          </View>
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator {...loadingProps} />
            </View>
          ) : null}
        </View>
      </TouchableOpacity>
    );

    return <View style={containerStyle}>{content}</View>;
  },
);

const styles = StyleSheet.create({
  content: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  contentLoading: {
    opacity: 0,
  },
  label: {
    lineHeight: 13,
  },
  labelledValue: {
    lineHeight: 18,
  },
  loadingContainer: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  textContainer: {
    alignItems: 'center',
    gap: 4,
  },
});
